import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { T } from '../lib/tokens';
import { supabase } from '../lib/supabase';
import { Thumb, CatBadge, TagChip, Confidence, TimeAgo, FavoriteStar } from './ui';
import type { MemoItem } from '../lib/api';

export function MosaicCard({ item }: { item: MemoItem }) {
  const isNote = item.kind === 'note';
  const tags = (item as any).tags as string[] | undefined;
  // chat メモは summary が無いので、会話込みダイジェストを要約として表示する。
  const sub = item.summary ?? item.digest;

  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  // メモの★はニュース記事と違い学習に紐づかないので、ml_items を直接更新してよい。
  async function toggleFavorite() {
    setBusy(true);
    try {
      await supabase.from('ml_items').update({ is_favorite: !item.is_favorite }).eq('id', item.id);
      qc.invalidateQueries({ queryKey: ['items'] });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Link
      to="/items/$itemId"
      params={{ itemId: item.id }}
      style={{ textDecoration: 'none' }}
    >
      <article style={{
        background: T.bgRaised,
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        cursor: 'pointer',
        transition: 'border-color 0.15s, transform 0.15s',
      }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLElement).style.borderColor = T.borderHi;
          (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLElement).style.borderColor = T.border;
          (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
        }}
      >
        {/* 大エリア（kind 別: link=画像 / note=箇条書き / image=添付） */}
        <Thumb
          catId={item.cat_id}
          title={item.title ?? item.original_content}
          kind={item.kind}
          ratio="16/10"
          imageUrl={item.og_image_url ?? item.image_url}
          summary={item.summary ?? item.original_content}
        />

        {/* 本文 */}
        <div style={{
          padding: 12, display: 'flex', flexDirection: 'column', gap: 7, flex: 1,
        }}>
          {/* メタ行 */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          }}>
            <CatBadge catId={item.cat_id} />
            <FavoriteStar active={!!item.is_favorite} busy={busy} onToggle={toggleFavorite} />
            {item.source_url && (
              <span style={{ fontSize: 11, fontFamily: T.fontMono, color: T.textMute }}>
                {new URL(item.source_url).hostname.replace('www.', '')}
              </span>
            )}
            <TimeAgo iso={item.created_at} />
            {item.rating != null && (
              <span title={`評価 ${item.rating}/5`} style={{ marginLeft: 'auto', fontSize: 11, color: T.accent, letterSpacing: 1 }}>
                {'★'.repeat(item.rating)}<span style={{ color: T.textMute }}>{'★'.repeat(5 - item.rating)}</span>
              </span>
            )}
          </div>

          {/* タイトル */}
          <div style={{
            fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {item.title ?? item.original_content.slice(0, 80)}
          </div>

          {/* 要約（note は大エリアに箇条書き表示済みのため省略 / chat は digest を表示） */}
          {!isNote && sub && (
            <div style={{
              fontSize: 12, color: T.textDim, lineHeight: 1.6,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {sub}
            </div>
          )}

          {/* フッター */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 5,
            flexWrap: 'wrap', marginTop: 'auto',
          }}>
            {tags?.slice(0, 3).map(t => <TagChip key={t} name={t} />)}
            <span style={{ marginLeft: 'auto' }}>
              <Confidence value={item.confidence} />
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}
